// The weapons on screen: the held guns on every cowboy (GunRack), and posed copies for the
// ordnance crates, the saloon gun rack and the lab (buildWeaponMesh). The models and their moving
// parts are in gunmodels.ts; they print through the robots' toon pipeline (render/robots), so a
// gun reads in the same ink, wear and glow as the appliances it shoots.
//
// Held guns: one instanced batch per weapon + finish (every cowboy holding it = one draw), the
// instance root at the gun bone's world pose (see the hook in render/cowboys/index.ts), each part
// posed from what that cowboy's gun is doing: shots, bursts, reload phase, spin, charge, aim.

import * as THREE from 'three';
import { Fx, TypeBatch } from '../render/robots/kit';
import { robotMaterial } from '../render/robots/material';
import { PAL } from '../render/palette';
import { Elem, W, WEAPONS } from '../../sim/weapons';
import { buildGunModel, ordnanceModel, type GunModel } from './gunmodels';
import { paintFor, restAct, type GunAct } from './gunkit';

export const ELEM_COLOR: Record<number, number> = {
  [Elem.None]: PAL.gearTag, [Elem.Fire]: 0xff7a2a, [Elem.Ice]: 0x7fd6ff, [Elem.Shock]: 0xf3e65a, [Elem.Acid]: 0x9be03a,
  [Elem.Magnetic]: 0xb07aff, [Elem.Rainbow]: PAL.primeMagenta,
};

const key = (id: number, fin: number) => id + fin * 256;
const models = new Map<number, GunModel>();
function modelOf(id: number, fin: number): GunModel {
  const k = key(id, fin);
  let m = models.get(k);
  if (!m) { m = buildGunModel(id, paintFor(fin)); models.set(k, m); }
  return m;
}
/** guns print like the robots, minus the ground grime (they never touch the ground) and with
 *  the grunge at hand scale, plus their years in a holster (material.ts FRAG_PATINA) */
const GUN_PRINT = { grungeScale: 2.4, yOff: 0.8, drip: 0.3, patina: 1 };
export const gunMat = (tex: THREE.DataTexture) => robotMaterial(tex, 1, GUN_PRINT);
const IDENT4 = new THREE.Matrix4();
function rimFx(seed: number) {
  const f = new Fx(), rim = new THREE.Color(PAL.sunCore);
  f.rim[0] = rim.r * 0.45; f.rim[1] = rim.g * 0.45; f.rim[2] = rim.b * 0.45;
  f.seed = seed;
  return f;
}
const restFx = rimFx(0.37);

// ------------------------------------------------------------------ posed copies
const statics = new Map<number, TypeBatch>();
function staticBatch(id: number, fin: number): TypeBatch {
  const k = key(id, fin);
  let b = statics.get(k);
  if (!b) {
    const m = modelOf(id, fin);
    b = new TypeBatch(m.rig.build(false), m.rig.slots, 1, true, `gun-${WEAPONS[id]?.short ?? id}`, gunMat);
    b.begin(); b.robot(IDENT4, restFx); m.pose(restAct(), b); b.end();
    statics.set(k, b);
  }
  return b;
}

/** A weapon in a gun finish, in its rest pose (shared geometry, parts and material per weapon + finish). */
export function buildWeaponMesh(id: number, finish = 0): THREE.Object3D {
  const wid = WEAPONS[id] ? id : W.Starter;
  const b = staticBatch(wid, finish);
  const m = new THREE.InstancedMesh(b.mesh.geometry, b.mesh.material, 1);
  m.customDepthMaterial = b.mesh.customDepthMaterial;
  m.castShadow = true; m.receiveShadow = true;
  m.frustumCulled = false;
  m.userData.shared = true;
  const g = new THREE.Group();
  g.name = `gun-${WEAPONS[wid]?.short ?? wid}`;
  g.add(m);
  g.userData.muzzle = modelOf(wid, finish).muzzle.clone();
  g.userData.weapon = wid;
  return g;
}

/** A gun for the saloon's gun case: its own batch (so the case light and the equipped glow never
 *  touch the crates' copies), lit for the rim and gleam from `light` (the direction to the case's
 *  downlight). `shine(k)` 0..1 is the equipped glow: a warm lift and a hotter rim. */
export function buildCaseWeapon(id: number, light: { value: THREE.Vector3 }): { obj: THREE.Object3D; shine(k: number): void } {
  const wid = WEAPONS[id] ? id : W.Starter;
  const m = modelOf(wid, 0);
  const b = new TypeBatch(m.rig.build(false), m.rig.slots, 1, true, `case-${WEAPONS[wid]?.short ?? wid}`,
    (tex) => robotMaterial(tex, 1, { ...GUN_PRINT, sunDir: light }));
  const f = new Fx();
  f.seed = 0.37 + wid * 0.173;
  const shine = (k: number) => {
    const r = 0.32 + k * 0.45;
    f.rim[0] = 1.0 * r; f.rim[1] = 0.8 * r; f.rim[2] = 0.55 * r;
    f.rim[3] = 0.55 - 0.4 * k; // the saloon key (the skylight) is strong: the case keeps its guns low, the equipped one lifts
    f.fx[0] = 0.13 * k; f.fx[1] = 0.075 * k; f.fx[2] = 0.025 * k;
    b.begin(); b.robot(IDENT4, f); m.pose(restAct(), b); b.end();
  };
  shine(0);
  b.mesh.frustumCulled = false;
  const g = new THREE.Group();
  g.name = `case-${WEAPONS[wid]?.short ?? wid}`;
  g.add(b.mesh);
  g.userData.muzzle = m.muzzle.clone();
  g.userData.weapon = wid;
  return { obj: g, shine };
}

/** An ordnance piece (gunmodels.ts ordnanceModel: rocket, harpoon, beacon, firework parts, the
 *  deployed turret) as a mesh, sharing geometry and material per piece and weapon. */
const pieces = new Map<string, TypeBatch>();
export function buildOrdnanceMesh(name: string, wid: number): THREE.Object3D {
  const k = `${name}:${wid}`;
  let b = pieces.get(k);
  if (!b) {
    const m = ordnanceModel(name, wid, paintFor(0));
    b = new TypeBatch(m.rig.build(false), m.rig.slots, 1, true, `ord-${k}`, gunMat);
    b.begin(); b.robot(IDENT4, restFx); m.pose(restAct(), b); b.end();
    pieces.set(k, b);
  }
  const mesh = new THREE.InstancedMesh(b.mesh.geometry, b.mesh.material, 1);
  mesh.customDepthMaterial = b.mesh.customDepthMaterial;
  mesh.castShadow = true; mesh.receiveShadow = true; mesh.frustumCulled = false;
  mesh.userData.shared = true; // owners must not dispose the shared material
  return mesh;
}

/** Barrel-tip offset in the model's local frame. */
export function weaponMuzzle(id: number) { return modelOf(WEAPONS[id] ? id : W.Starter, 0).muzzle; }

// ------------------------------------------------------------------ on the cowboys
/** what the cowboy view knows about this cowboy's gun this frame (render/cowboys/index.ts) */
export interface HeldAct { reload: number; u: number; n: number; shotAge: number; ads: number }

interface Held {
  wid: number; fin: number; seen: boolean;
  act: GunAct;
  lastAge: number; lastShotT: number; reloading: boolean; reloadBase: number;
}
const _m = new THREE.Matrix4(), _p = new THREE.Vector3(), _q = new THREE.Quaternion(), _s = new THREE.Vector3();
const HELD_CAP = 8;

export class GunRack {
  private held = new Map<number, Held>();
  private batches = new Map<number, TypeBatch>();
  private group = new THREE.Group();
  private fx = rimFx(0);
  private t = 0;
  private dt = 0;
  private last = -1;
  /** your own cowboy's exact gun state (WeaponsClient): rounds, spin 0..1, charge 0..1 */
  private me = { id: -1, mag: 0, spin: 0, charge: 0 };
  /** player id → gun finish (their Service Record look, from the roster) */
  finishOf: (id: number) => number = () => 0;

  constructor(scene: THREE.Object3D) {
    this.group.name = 'held-guns';
    scene.add(this.group);
  }

  /** your own cowboy: the sim's exact rounds, spin and charge (the others are read from their shots) */
  local(id: number, mag: number, spin: number, charge: number) {
    this.me.id = id; this.me.mag = mag; this.me.spin = spin; this.me.charge = charge;
  }

  begin() {
    const now = performance.now() / 1000;
    this.dt = this.last < 0 ? 0 : Math.min(0.1, now - this.last);
    this.last = now;
    this.t += this.dt;
    for (const b of this.batches.values()) b.begin();
    for (const h of this.held.values()) h.seen = false;
  }

  private batch(wid: number, fin: number): TypeBatch {
    const k = key(wid, fin);
    let b = this.batches.get(k);
    if (!b) {
      const m = modelOf(wid, fin);
      const geo = m.rig.build(true);
      b = new TypeBatch(geo, m.rig.slots, HELD_CAP, true, `held-${WEAPONS[wid]?.short ?? wid}`, gunMat, m.rig.mainCount);
      this.group.add(b.mesh);
      this.batches.set(k, b);
    }
    return b;
  }

  /** read this frame's shots and reload into the gun's action state */
  private track(id: number, h: Held, act: HeldAct | undefined) {
    const a = h.act, d = WEAPONS[h.wid];
    const age = act ? act.shotAge : 99;
    a.t = this.t;
    a.ads = act ? act.ads : 0;
    if (age + 1e-4 < h.lastAge && age < 0.25) {
      a.shots++;
      a.burst = this.t - h.lastShotT < 0.16 ? a.burst + 1 : 1;
      h.lastShotT = this.t;
      a.loaded = Math.max(0, a.loaded - 1);
    }
    h.lastAge = age;
    a.shotAge = age;
    const r = act ? act.reload : 0;
    a.reload = r; a.reloadU = act ? act.u : 0; a.reloadN = act ? act.n : 0;
    const now = r > 0.5;
    if (now && !h.reloading) h.reloadBase = a.loaded;
    if (now && d.rounds) a.loaded = Math.min(d.mag, h.reloadBase + a.reloadN);
    if (!now && h.reloading) { a.loaded = d.mag; a.shots = 0; }
    h.reloading = now;
    if (d.slot === 2 && age > 2) a.loaded = Math.max(a.loaded, 1); // specials recharge
    if (id === this.me.id) {
      a.loaded = this.me.mag;
      a.spin = this.me.spin;
      a.charge = this.me.charge;
    } else {
      const want = age < 0.25 && h.wid === W.AutoMG ? 1 : 0;
      a.spin += (want - a.spin) * Math.min(1, this.dt * (want > a.spin ? 1.4 : 2.2));
      a.charge = 0;
    }
    a.turn = (a.turn + a.spin * 4.2 * this.dt) % 64;
  }

  /**
   * Hook for CowboyView: draw this cowboy's weapon at the gun bone's world pose (and an off-hand
   * twin for akimbo). Writes the muzzle into `muzzleOut`. Returns true when it drew a gun, so the
   * baked class gun should hide.
   */
  place = (id: number, wid: number, gunWorld: THREE.Matrix4, leftHand: THREE.Matrix4 | null, muzzleOut: THREE.Vector3, visible: boolean, act?: HeldAct): boolean => {
    if (!WEAPONS[wid]) return false;
    const fin = this.finishOf(id);
    let h = this.held.get(id);
    if (!h || h.wid !== wid || h.fin !== fin) {
      const a = restAct();
      a.loaded = WEAPONS[wid].mag;
      h = { wid, fin, seen: true, act: a, lastAge: 99, lastShotT: -9, reloading: false, reloadBase: 0 };
      this.held.set(id, h);
    }
    h.seen = true;
    this.track(id, h, act);
    const m = modelOf(wid, fin);
    muzzleOut.copy(m.muzzle).applyMatrix4(gunWorld);
    if (!visible) return true;
    const b = this.batch(wid, fin);
    this.fx.seed = (id * 0.618) % 1;
    h.act.twin = false;
    if (b.robot(gunWorld, this.fx)) m.pose(h.act, b);
    if (WEAPONS[wid].akimbo && leftHand) {
      // same aim as the main gun, sitting in the left hand
      gunWorld.decompose(_p, _q, _s);
      _p.setFromMatrixPosition(leftHand);
      _m.compose(_p, _q, _s);
      h.act.twin = true;
      if (b.robot(_m, this.fx)) m.pose(h.act, b);
    }
    return true;
  };

  /** what this cowboy is holding and doing this frame (after place() ran for them), or null */
  heldOf(id: number): { wid: number; fin: number; act: GunAct } | null {
    const h = this.held.get(id);
    return h && h.seen ? h : null;
  }

  /** Draw this cowboy's held gun at `root` instead of the gun bone (the first-person view model,
   *  viewmodel.ts), sharing the gun's batch and action state, and write its muzzle. */
  drawAt(id: number, root: THREE.Matrix4, muzzleOut: THREE.Vector3, twin = false): boolean {
    const h = this.held.get(id);
    if (!h || !h.seen) return false;
    const m = modelOf(h.wid, h.fin), b = this.batch(h.wid, h.fin);
    muzzleOut.copy(m.muzzle).applyMatrix4(root);
    this.fx.seed = (id * 0.618) % 1;
    h.act.twin = twin;
    if (b.robot(root, this.fx)) m.pose(h.act, b);
    return true;
  }

  /** a gun's muzzle in its own frame */
  muzzleOf(wid: number) { return modelOf(wid, 0).muzzle; }

  end() {
    for (const b of this.batches.values()) b.end();
    for (const [id, h] of this.held) if (!h.seen) this.held.delete(id);
  }
}
